import React, { createContext, useContext, useState, useEffect } from 'react';
import { db, collection, query, where, getDocs, doc, getDoc } from '../../firebase';

// Contexto para el sistema de permisos
const PermissionContext = createContext();

// Hook para usar el contexto de permisos
export const usePermissions = () => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissions debe ser usado dentro de un PermissionProvider');
  }
  return context;
};

// Proveedor del contexto de permisos
export const PermissionProvider = ({ children, user }) => {
  const [userPermissions, setUserPermissions] = useState([]);
  const [userGroups, setUserGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      loadUserPermissions();
    } else {
      setUserPermissions([]);
      setUserGroups([]);
      setLoading(false);
    }
  }, [user]);

  const loadUserPermissions = async () => {
    if (!user || !user.email) {
      setUserPermissions([]);
      setUserGroups([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      // Si es admin, tiene todos los permisos
      if (user.role === 'admin') {
        setUserPermissions(['gestion', 'correos', 'clases', 'reservas', 'incidencias', 'admin']);
        setUserGroups([]);
        setLoading(false);
        return;
      }

      // 1. Obtener permisos del rol principal
      let rolePermissions = [];
      if (user.role !== 'user') {
        const roleDoc = await getDoc(doc(db, 'roles', user.role));
        if (roleDoc.exists()) {
          const roleData = roleDoc.data();
          rolePermissions = roleData.permissions || [];
        }
      } else {
        // Usuario estándar sin permisos - usar grupos para asignar
        rolePermissions = [];
      }

      // 2. Obtener permisos adicionales de grupos
      const groupsSnapshot = await getDocs(collection(db, 'groups'));
      const userGroupsList = [];
      const groupPermissions = new Set(rolePermissions);

      for (const groupDoc of groupsSnapshot.docs) {
        const groupData = groupDoc.data();
        const groupOUs = groupData.organizationalUnits || [];

        // Verificar si el usuario pertenece a alguna OU del grupo
        // ✅ ARREGLADO: Usar 'orgUnit' que es como se guarda en Firestore
        let userBelongsToGroup = false;
        const userOU = user.orgUnit || user.orgUnitPath; // Soportar ambos por compatibilidad

        if (userOU && groupOUs.length > 0) {
          userBelongsToGroup = groupOUs.some(groupOU => {
            return userOU === groupOU || userOU.startsWith(groupOU + '/');
          });
        }

        // Si el usuario pertenece al grupo por OU o tiene permisos custom
        if (userBelongsToGroup || (groupData.customPermissions && groupData.customPermissions[user.email])) {
          userGroupsList.push({
            id: groupDoc.id,
            ...groupData
          });

          // Agregar permisos base de los roles del grupo (solo si pertenece por OU)
          if (userBelongsToGroup && groupData.roles && groupData.roles.length > 0) {
            for (const roleId of groupData.roles) {
              const groupRoleDoc = await getDoc(doc(db, 'roles', roleId));
              if (groupRoleDoc.exists()) {
                const groupRoleData = groupRoleDoc.data();
                (groupRoleData.permissions || []).forEach(perm => groupPermissions.add(perm));
              }
            }
          }

          // Agregar permisos custom del usuario en este grupo (si los tiene)
          if (groupData.customPermissions && groupData.customPermissions[user.email]) {
            const customData = groupData.customPermissions[user.email];
            const customPerms = typeof customData === 'object' && customData.permissions
              ? customData.permissions
              : (Array.isArray(customData) ? customData : []);

            customPerms.forEach(perm => groupPermissions.add(perm));
          }
        }
      }

      setUserPermissions(Array.from(groupPermissions));
      setUserGroups(userGroupsList);
    } catch (error) {
      console.error('Error cargando permisos:', error);
      // Sin permisos por defecto en caso de error
      setUserPermissions([]);
      setUserGroups([]);
    } finally {
      setLoading(false);
    }
  };

  // Función para verificar si el usuario tiene un permiso específico
  const hasPermission = (permission) => {
    if (!user) return false;
    if (user.role === 'admin') return true; // Admin tiene todos los permisos
    return userPermissions.includes(permission);
  };

  // Función para verificar múltiples permisos (AND)
  const hasAllPermissions = (permissions) => {
    return permissions.every(permission => hasPermission(permission));
  };

  // Función para verificar si tiene al menos uno de los permisos (OR)
  const hasAnyPermission = (permissions) => {
    return permissions.some(permission => hasPermission(permission));
  };

  // Función para verificar si es admin
  const isAdmin = () => {
    return user?.role === 'admin';
  };

  // Función para obtener todos los permisos del usuario
  const getAllPermissions = () => {
    return userPermissions;
  };

  const value = {
    userPermissions,
    userGroups,
    loading,
    hasPermission,
    hasAllPermissions,
    hasAnyPermission,
    isAdmin,
    getAllPermissions,
    refreshPermissions: loadUserPermissions
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};

// Componente para proteger rutas basado en permisos
export const ProtectedRoute = ({ 
  children, 
  permission, 
  permissions = [], 
  requireAll = false,
  fallback = null 
}) => {
  const { hasPermission, hasAllPermissions, hasAnyPermission, loading } = usePermissions();

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '200px',
        flexDirection: 'column',
        gap: '12px'
      }}>
        <div style={{
          width: '32px',
          height: '32px',
          border: '3px solid #f3f3f3',
          borderTop: '3px solid #3182ce',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <div style={{ color: '#6b7280', fontSize: '14px' }}>
          Verificando permisos...
        </div>
      </div>
    );
  }

  let hasAccess = false;

  if (permission) {
    hasAccess = hasPermission(permission);
  } else if (permissions.length > 0) {
    hasAccess = requireAll ? hasAllPermissions(permissions) : hasAnyPermission(permissions);
  }

  if (!hasAccess) {
    return fallback || (
      <div style={{
        textAlign: 'center',
        padding: '60px 20px',
        color: '#6b7280'
      }}>
        <div style={{
          fontSize: '48px',
          marginBottom: '16px'
        }}>🔒</div>
        <h3 style={{
          fontSize: '20px',
          fontWeight: '600',
          color: '#374151',
          margin: '0 0 8px 0'
        }}>
          Acceso Denegado
        </h3>
        <p style={{
          fontSize: '14px',
          color: '#6b7280',
          margin: '0',
          maxWidth: '400px',
          marginLeft: 'auto',
          marginRight: 'auto'
        }}>
          No tienes permisos para acceder a esta sección. 
          Contacta con tu administrador si crees que deberías tener acceso.
        </p>
      </div>
    );
  }

  return children;
};

// Componente para mostrar/ocultar elementos basado en permisos
export const PermissionGate = ({ 
  children, 
  permission, 
  permissions = [], 
  requireAll = false,
  fallback = null 
}) => {
  const { hasPermission, hasAllPermissions, hasAnyPermission, loading } = usePermissions();

  // Durante la carga, no mostrar nada por defecto
  if (loading) {
    return null;
  }

  let hasAccess = false;

  if (permission) {
    hasAccess = hasPermission(permission);
  } else if (permissions.length > 0) {
    hasAccess = requireAll ? hasAllPermissions(permissions) : hasAnyPermission(permissions);
  } else {
    // Si no se especifica ningún permiso, mostrar siempre
    hasAccess = true;
  }

  if (!hasAccess) {
    return fallback;
  }

  return children;
};

// Hook personalizado para verificar permisos específicos del departamento
export const useDepartmentPermissions = () => {
  const [isAdminDepartamento, setIsAdminDepartamento] = useState(false);
  const [loading, setLoading] = useState(true);

  const checkAdminDepartamento = async (userEmail) => {
    if (!userEmail) {
      setIsAdminDepartamento(false);
      setLoading(false);
      return;
    }

    try {
      const adminQuery = query(
        collection(db, 'admins_departamento'),
        where('email', '==', userEmail)
      );
      const adminSnapshot = await getDocs(adminQuery);
      setIsAdminDepartamento(!adminSnapshot.empty);
    } catch (error) {
      console.error('Error verificando admin de departamento:', error);
      setIsAdminDepartamento(false);
    } finally {
      setLoading(false);
    }
  };

  return {
    isAdminDepartamento,
    loading,
    checkAdminDepartamento
  };
};

// Utilidad para obtener la lista de roles disponibles
export const useRoles = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadRoles();
  }, []);

  const loadRoles = async () => {
    try {
      const rolesSnapshot = await getDocs(collection(db, 'roles'));
      const rolesList = rolesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setRoles(rolesList);
    } catch (error) {
      console.error('Error cargando roles:', error);
    } finally {
      setLoading(false);
    }
  };

  return {
    roles,
    loading,
    refreshRoles: loadRoles
  };
};

// Hook para obtener permisos granulados del usuario actual (rol + grupos)
export const useUserPermissionsGranular = () => {
  const [granularPermissions, setGranularPermissions] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user } = useContext(require('../../AuthContext').AuthContext);

  useEffect(() => {
    if (user) {
      loadGranularPermissions();
    } else {
      setGranularPermissions(null);
      setLoading(false);
    }
  }, [user]);

  const loadGranularPermissions = async () => {
    if (!user || !user.email) {
      setGranularPermissions(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      // Si es admin, todos los permisos completos
      if (user.role === 'admin') {
        setGranularPermissions({
          basePermissions: ['gestion', 'correos', 'clases', 'reservas', 'incidencias', 'admin'],
          emailPermissions: {
            allowedOUs: [],
            canManageAll: true
          },
          classPermissions: {
            allowedGroups: [],
            canManageAll: true
          },
          reservationPermissions: {
            advancedPermissions: ['createSpaces', 'editSpaces', 'deleteSpaces', 'createPeriodicReservations']
          },
          groups: []
        });
        setLoading(false);
        return;
      }

      // 1. Obtener permisos del rol principal
      let roleData = null;
      if (user.role !== 'user') {
        const roleDoc = await getDoc(doc(db, 'roles', user.role));
        if (roleDoc.exists()) {
          roleData = roleDoc.data();
        }
      }

      // Permisos base del rol (sin permisos por defecto)
      const basePermissions = roleData?.permissions || [];
      const emailPermissions = roleData?.emailPermissions || { allowedOUs: [], canManageAll: false };
      const classPermissions = roleData?.classPermissions || { allowedGroups: [], canManageAll: false };
      const reservationPermissions = roleData?.reservationPermissions || { advancedPermissions: [] };

      // 2. Obtener grupos y permisos adicionales
      const groupsSnapshot = await getDocs(collection(db, 'groups'));
      const userGroupsList = [];

      // Conjuntos para acumular permisos
      const allPermissions = new Set(basePermissions);
      const allEmailOUs = new Set(emailPermissions.allowedOUs || []);
      const allClassGroups = new Set(classPermissions.allowedGroups || []);
      const allReservationPerms = new Set(reservationPermissions.advancedPermissions || []);

      let canManageAllEmails = emailPermissions.canManageAll || false;
      let canManageAllClasses = classPermissions.canManageAll || false;

      for (const groupDoc of groupsSnapshot.docs) {
        const groupData = groupDoc.data();
        const groupOUs = groupData.organizationalUnits || [];

        // Verificar si el usuario pertenece al grupo por OU
        // ✅ ARREGLADO: Usar 'orgUnit' que es como se guarda en Firestore
        let userBelongsToGroup = false;
        const userOU = user.orgUnit || user.orgUnitPath; // Soportar ambos por compatibilidad

        if (userOU && groupOUs.length > 0) {
          userBelongsToGroup = groupOUs.some(groupOU => {
            return userOU === groupOU || userOU.startsWith(groupOU + '/');
          });
        }

        // Verificar si tiene permisos custom en este grupo
        const hasCustomPermissions = groupData.customPermissions && groupData.customPermissions[user.email];

        if (userBelongsToGroup || hasCustomPermissions) {
          userGroupsList.push({
            id: groupDoc.id,
            ...groupData
          });

          // Agregar permisos de los roles del grupo (si pertenece por OU)
          if (userBelongsToGroup && groupData.roles && groupData.roles.length > 0) {
            for (const roleId of groupData.roles) {
              const groupRoleDoc = await getDoc(doc(db, 'roles', roleId));
              if (groupRoleDoc.exists()) {
                const groupRoleData = groupRoleDoc.data();

                // Permisos base
                (groupRoleData.permissions || []).forEach(perm => allPermissions.add(perm));

                // Permisos de email
                if (groupRoleData.emailPermissions) {
                  if (groupRoleData.emailPermissions.canManageAll) {
                    canManageAllEmails = true;
                  }
                  (groupRoleData.emailPermissions.allowedOUs || []).forEach(ou => allEmailOUs.add(ou));
                }

                // Permisos de clases
                if (groupRoleData.classPermissions) {
                  if (groupRoleData.classPermissions.canManageAll) {
                    canManageAllClasses = true;
                  }
                  (groupRoleData.classPermissions.allowedGroups || []).forEach(g => allClassGroups.add(g));
                }

                // Permisos de reservas
                if (groupRoleData.reservationPermissions) {
                  (groupRoleData.reservationPermissions.advancedPermissions || []).forEach(p => allReservationPerms.add(p));
                }
              }
            }
          }

          // Agregar permisos custom del usuario (si los tiene)
          if (hasCustomPermissions) {
            const customData = groupData.customPermissions[user.email];
            const customPerms = typeof customData === 'object' && customData.permissions
              ? customData.permissions
              : (Array.isArray(customData) ? customData : []);

            customPerms.forEach(perm => allPermissions.add(perm));

            // Permisos granulados custom
            if (typeof customData === 'object') {
              // Email permissions
              if (customData.emailPermissions) {
                if (customData.emailPermissions.canManageAll) {
                  canManageAllEmails = true;
                }
                (customData.emailPermissions.allowedOUs || []).forEach(ou => allEmailOUs.add(ou));
              }

              // Class permissions
              if (customData.classPermissions) {
                if (customData.classPermissions.canManageAll) {
                  canManageAllClasses = true;
                }
                (customData.classPermissions.allowedGroups || []).forEach(g => allClassGroups.add(g));
              }

              // Reservation permissions
              if (customData.reservationPermissions) {
                (customData.reservationPermissions.advancedPermissions || []).forEach(p => allReservationPerms.add(p));
              }
            }
          }
        }
      }

      setGranularPermissions({
        basePermissions: Array.from(allPermissions),
        emailPermissions: {
          allowedOUs: Array.from(allEmailOUs),
          canManageAll: canManageAllEmails
        },
        classPermissions: {
          allowedGroups: Array.from(allClassGroups),
          canManageAll: canManageAllClasses
        },
        reservationPermissions: {
          advancedPermissions: Array.from(allReservationPerms)
        },
        groups: userGroupsList
      });
    } catch (error) {
      console.error('Error cargando permisos granulados:', error);
      setGranularPermissions(null);
    } finally {
      setLoading(false);
    }
  };

  return {
    granularPermissions,
    loading,
    refreshGranularPermissions: loadGranularPermissions
  };
};

// Componente de ejemplo para mostrar permisos del usuario actual
export const UserPermissionsDisplay = () => {
  const { userPermissions, loading, isAdmin } = usePermissions();

  if (loading) {
    return <div style={{ fontSize: '14px', color: '#6b7280' }}>Cargando permisos...</div>;
  }

  const containerStyle = {
    background: '#f8fafc',
    padding: '16px',
    borderRadius: '8px',
    border: '1px solid #e2e8f0'
  };

  const titleStyle = {
    fontSize: '16px',
    fontWeight: '600',
    marginBottom: '12px',
    color: '#2d3748',
    margin: '0 0 12px 0'
  };

  const listStyle = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px'
  };

  const tagStyle = {
    backgroundColor: '#ebf8ff',
    color: '#2b6cb0',
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: '500'
  };

  const adminTagStyle = {
    backgroundColor: '#fed7d7',
    color: '#742a2a',
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: '500'
  };

  return (
    <div style={containerStyle}>
      <h4 style={titleStyle}>Tus Permisos:</h4>
      <div style={listStyle}>
        {isAdmin() ? (
          <span style={adminTagStyle}>ADMINISTRADOR (Todos los permisos)</span>
        ) : userPermissions.length > 0 ? (
          userPermissions.map(permission => (
            <span key={permission} style={tagStyle}>
              {permission}
            </span>
          ))
        ) : (
          <span style={{ color: '#6b7280', fontSize: '12px', fontStyle: 'italic' }}>
            Sin permisos asignados
          </span>
        )}
      </div>
    </div>
  );
};